import React, { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import GuestLayout from '../../components/layouts/guest_layout'
import FlatButton from '../../components/buttons/flat_button'
import { FormErrorMessage } from '../../constants/message'
import {
  FormField,
  TextInput,
  TextareaInput,
} from '../../components/form'
import { getKyousouApiClient, handleError, ApiErrorResponse } from '../../services/network'
import { useSnackBar } from '../../providers/snack_bar_provider'

type ContactFormProps = {
  enterpriseName: string | null
  departmentName: string | null
  name: string
  email: string
  tel: string | null
  subject: string
  message: string
  privacyPolicyAgreed: boolean
}

const ContactPage: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showSnackBar } = useSnackBar()

  // ページ読み込み時にトップにスクロール
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const initialValues = (location.state as ContactFormProps) || {
    enterpriseName: '',
    departmentName: '',
    name: '',
    email: '',
    tel: '',
    subject: '',
    message: '',
    privacyPolicyAgreed: false,
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactFormProps>({
    defaultValues: initialValues,
    mode: 'onSubmit',
  })

  const onSubmit = async (data: ContactFormProps) => {
    try {
      const apiClient = getKyousouApiClient()
      
      // サーバー側のバリデーションチェック
      await apiClient.contact.confirmCreate({
        enterpriseName: data.enterpriseName || undefined,
        departmentName: data.departmentName || undefined,
        name: data.name,
        email: data.email,
        tel: data.tel || undefined,
        subject: data.subject,
        message: data.message,
      })
      
      // バリデーションが成功したら確認画面へ遷移
      navigate('/contact/confirm', { state: data })
    } catch (e) {
      // バリデーションエラーの場合はエラーを表示して処理を終了
      handleError(e as ApiErrorResponse, showSnackBar)
    }
  }

  return (
    <GuestLayout>
      <div className="max-w-5xl w-full mx-auto">
        <div className="px-4 sm:px-8 py-8">
          <h1 className="font-bold text-2xl mb-6">お問い合わせ</h1>
          
          <div className="bg-white rounded-xl py-8">
            <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl mx-auto px-4 sm:px-8">
                {/* 企業名・団体名 */}
                <FormField label="企業名・団体名" required={false}>
                  <TextInput
                    register={register('enterpriseName')}
                    placeholder="例：株式会社〇〇"
                  />
                </FormField>

                {/* 部署名 */}
                <FormField label="部署名" required={false}>
                  <TextInput
                    register={register('departmentName')}
                    placeholder="例：営業部"
                  />
                </FormField>

                {/* 名前（必須） */}
                <FormField label="名前" required>
                  <TextInput
                    register={register('name', {
                      required: FormErrorMessage.required,
                    })}
                    placeholder="例：山田太郎"
                  />
                  {errors.name && (
                    <p className="text-sm text-main-red mt-1">{errors.name.message}</p>
                  )}
                </FormField>

                {/* メールアドレス（必須） */}
                <FormField label="メールアドレス" required>
                  <TextInput
                    type="email"
                    register={register('email', {
                      required: FormErrorMessage.required,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: '有効なメールアドレスを入力してください',
                      },
                    })}
                    placeholder="例：example@example.com"
                  />
                  {errors.email && (
                    <p className="text-sm text-main-red mt-1">{errors.email.message}</p>
                  )}
                </FormField>

                {/* 電話番号 */}
                <FormField label="電話番号" required={false}>
                  <TextInput
                    register={register('tel')}
                    placeholder="例：03-1234-5678"
                  />
                </FormField>

                {/* 件名（必須） */}
                <FormField label="件名" required>
                  <TextInput
                    register={register('subject', {
                      required: FormErrorMessage.required,
                    })}
                    placeholder="お問い合わせの件名を入力してください"
                  />
                  {errors.subject && (
                    <p className="text-sm text-main-red mt-1">{errors.subject.message}</p>
                  )}
                </FormField>

                {/* お問い合わせ本文（必須） */}
                <FormField label="お問い合わせ本文" required>
                  <TextareaInput
                    register={register('message', {
                      required: FormErrorMessage.required,
                    })}
                    rows={6}
                    placeholder="お問い合わせ内容を入力してください"
                  />
                  {errors.message && (
                    <p className="text-sm text-main-red mt-1">{errors.message.message}</p>
                  )}
                </FormField>

              {/* プライバシーポリシー */}
              <div className="mt-8">
                <h3 className="font-bold text-lg mb-4">プライバシーポリシーについて</h3>
                <div className="mb-6">
                  <Link
                    to="/policy/privacy"
                    target="_blank"
                    className="text-main-green underline hover:opacity-80"
                  >
                    新規タブを開いて、プライバシーポリシーを確認する
                  </Link>
                </div>
                <div className="border border-black rounded-lg p-6 max-h-[300px] overflow-y-auto mb-6 bg-main-white">
                  <p className="text-sm leading-relaxed">
                    株式会社The Lodges（以下、「当社」といいます）は、お客様の個人情報を取り扱うにあたり、<br />
                    「個人情報の保護に関する法令」を遵守し、個人の権利利益を不当に侵害することがないようにプ<br />
                    ライバシーポリシー（個人情報保護規定）を定めています。<br />
                    当社は、当社サービスを提供する際にこの規定に基づき、企業・団体およびメディア等の方々（以下<br />
                    「お客様」という）の個人情報を取り扱います。
                  </p>
                </div>
                
                <div className="flex items-center justify-center mb-8">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('privacyPolicyAgreed', {
                        required: true,
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      上記のプライバシーポリシーについて同意する
                    </span>
                  </label>
                </div>
                {errors.privacyPolicyAgreed && (
                  <p className="text-sm text-main-red text-center mb-4">
                    プライバシーポリシーへの同意は必須です
                  </p>
                )}
              </div>

              {/* ボタン */}
              <div className="grid grid-cols-2 gap-6 mt-8 max-w-md mx-auto">
                <FlatButton
                  type="button"
                  onClick={() => navigate('/')}
                  outline
                  className="py-3"
                >
                  TOPページへ戻る
                </FlatButton>
                <FlatButton
                  type="submit"
                  className="py-3"
                >
                  確認する
                </FlatButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </GuestLayout>
  )
}

export default ContactPage